import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from "react";
import { Select } from "components/base/Form";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import { Alert } from "components/base/Toast";
import {
  getModelConfig,
  availableOperators,
  stateOptions,
  operatorsByFieldType,
} from "../utils/constants";

import {
  getFieldType,
  splitAndFilter,
  buildOdooDomainExpression,
} from "../utils/helpers";

// 基本条件面板子组件
const BasicConditions = ({
  initialCompanyId = "3",
  initialTimeRange = "twoWeeks",
  hasCustomConditions = false,
  onCompanyChange,
  onTimeRangeChange,
}) => {
  // 内部状态管理
  const [companyId, setCompanyId] = useState(initialCompanyId);
  const [queryTimeRange, setQueryTimeRange] = useState(initialTimeRange);

  // 当内部状态变化时通知父组件
  useEffect(() => {
    if (onCompanyChange) {
      onCompanyChange(companyId);
    }
  }, [companyId, onCompanyChange]);

  useEffect(() => {
    if (onTimeRangeChange) {
      onTimeRangeChange(queryTimeRange);
    }
  }, [queryTimeRange, onTimeRangeChange]);

  // 公司ID变更处理
  const handleCompanyChange = (e) => {
    setCompanyId(e.target.value);
  };

  // 时间范围变更处理
  const handleTimeRangeChange = (e) => {
    setQueryTimeRange(e.target.value);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 pb-4 border-b border-blue-100">
      <div className="flex items-center space-x-2">
        <label className="text-gray-700">系统:</label>
        <Select
          value={companyId}
          onChange={handleCompanyChange}
          className="flex-grow"
        >
          <option value="1">AB</option>
          <option value="3">LTD</option>
        </Select>
      </div>
      <div className="flex items-center space-x-2">
        <label className="text-gray-700">查询范围:</label>
        <Select
          value={queryTimeRange}
          onChange={handleTimeRangeChange}
          className="flex-grow"
        >
          <option value="twoWeeks">近两周</option>
          <option value="month">近一个月</option>
          <option value="all">全部记录</option>
        </Select>
        {queryTimeRange !== "all" && !hasCustomConditions && (
          <span className="text-xs text-gray-500 ml-2">
            (默认查询近两周内的记录)
          </span>
        )}
      </div>
    </div>
  );
};

// 条件列表子组件
const ConditionsList = ({ conditions, modelName, onDelete }) => {
  // 获取模型配置
  const modelConfig = getModelConfig(modelName);
  const availableFields = modelConfig.availableFields;

  if (conditions.length === 0) return null;

  return (
    <div className="mb-4 grid grid-cols-1 gap-2">
      <h4 className="text-sm font-medium text-gray-700 mb-2">
        已添加条件{" "}
        <span className="text-xs text-green-600">({conditions.length})</span>
      </h4>
      {conditions.map((condition) => (
        <div
          key={condition.id}
          className="flex items-center bg-green-50 p-2 rounded border border-green-200"
        >
          <span className="text-sm flex-grow">
            <span className="font-medium">{condition.field}</span>{" "}
            <span className="text-gray-500">{condition.operator}</span>{" "}
            <span className="font-medium">
              {Array.isArray(condition.value)
                ? `[${condition.value.join(", ")}]`
                : condition.value}
            </span>
            <span className="text-xs text-gray-500 ml-2">
              (
              {availableFields.find((f) => f.value === condition.field)
                ?.label || ""}
              )
            </span>
          </span>
          <button
            onClick={() => onDelete(condition.id)}
            className="text-red-500 hover:text-red-700 p-1"
            title="删除条件"
          >
            <FiTrash2 size={16} />
          </button>
        </div>
      ))}
    </div>
  );
};

// 添加条件表单子组件
const AddConditionForm = ({ modelName, onAddCondition }) => {
  // 获取模型配置
  const modelConfig = getModelConfig(modelName);
  const availableFields = modelConfig.availableFields;

  const [newCondition, setNewCondition] = useState({
    field: "name",
    operator: "ilike",
    value: "",
  });

  // 根据字段类型获取可用的操作符
  const getFilteredOperators = (fieldName) => {
    const fieldType = getFieldType(fieldName, modelName);
    const allowedOperatorValues =
      operatorsByFieldType[fieldType] || operatorsByFieldType.string;
    return availableOperators.filter((op) =>
      allowedOperatorValues.includes(op.value),
    );
  };

  // 当前字段可用的操作符
  const [filteredOperators, setFilteredOperators] = useState(() =>
    getFilteredOperators("name"),
  );

  // 当字段变化时更新可用的操作符
  useEffect(() => {
    const operators = getFilteredOperators(newCondition.field);
    setFilteredOperators(operators);

    // 如果当前操作符不在可用列表中，则更新为第一个可用的操作符
    if (!operators.some((op) => op.value === newCondition.operator)) {
      setNewCondition((prev) => ({
        ...prev,
        operator: operators[0]?.value || "=",
      }));
    }
  }, [newCondition.field, modelName]);

  // 输入字段更新处理函数
  const handleInputChange = (field, value) => {
    setNewCondition({ ...newCondition, [field]: value });
  };

  // 处理添加新条件
  const handleAddCondition = () => {
    if (!newCondition.value.trim()) {
      Alert("warning", "请输入条件值");
      return;
    }

    // 格式化条件值
    let formattedValue = newCondition.value;

    // 处理"in"操作符和类型转换
    if (newCondition.operator === "in") {
      formattedValue = splitAndFilter(newCondition.value);
      if (formattedValue.length === 1) formattedValue = formattedValue[0];
    }

    // 根据字段类型处理值
    const fieldType = getFieldType(newCondition.field);
    if (
      fieldType === "number" &&
      (newCondition.operator === "=" || newCondition.operator === "in")
    ) {
      formattedValue = Array.isArray(formattedValue)
        ? formattedValue.map((v) => parseInt(v))
        : parseInt(formattedValue);
    } else if (fieldType === "boolean" && newCondition.operator === "=") {
      formattedValue = formattedValue.toLowerCase() === "true";
    }

    // 调用父组件的添加函数
    onAddCondition({ ...newCondition, value: formattedValue, id: Date.now() });

    // 重置表单
    setNewCondition({ field: "name", operator: "ilike", value: "" });
  };

  // 渲染值输入控件
  const renderValueInput = () => {
    const fieldType = getFieldType(newCondition.field);

    // 根据字段类型渲染不同的输入控件
    switch (fieldType) {
      case "state":
        return (
          <Select
            value={newCondition.value}
            onChange={(e) => handleInputChange("value", e.target.value)}
            className="flex-grow"
          >
            <option value="">选择状态</option>
            {stateOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        );

      case "boolean":
        if (newCondition.operator === "=") {
          return (
            <Select
              value={newCondition.value}
              onChange={(e) => handleInputChange("value", e.target.value)}
              className="flex-grow"
            >
              <option value="">选择值</option>
              <option value="true">是</option>
              <option value="false">否</option>
            </Select>
          );
        }
        break;

      case "date":
        if ([">=", "<="].includes(newCondition.operator)) {
          return (
            <input
              type="datetime-local"
              value={newCondition.value}
              onChange={(e) => handleInputChange("value", e.target.value)}
              className="border rounded p-2 flex-grow"
            />
          );
        }
        break;
    }

    // 默认文本输入
    return (
      <input
        type="text"
        value={newCondition.value}
        onChange={(e) => handleInputChange("value", e.target.value)}
        placeholder={
          newCondition.operator === "in" ? "多个值用英文逗号分隔" : "输入条件值"
        }
        className="border rounded p-2 flex-grow"
      />
    );
  };

  return (
    <div>
      <h4 className="text-sm font-medium text-gray-700 mb-2">添加新条件</h4>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-center">
        <div className="md:col-span-2">
          <Select
            value={newCondition.field}
            onChange={(e) => handleInputChange("field", e.target.value)}
            className="w-full"
          >
            {availableFields.map((field) => (
              <option key={field.value} value={field.value}>
                {field.value} ({field.label})
              </option>
            ))}
          </Select>
        </div>

        <div>
          <Select
            value={newCondition.operator}
            onChange={(e) => handleInputChange("operator", e.target.value)}
            className="w-full"
          >
            {filteredOperators.map((op) => (
              <option key={op.value} value={op.value}>
                {op.label}
              </option>
            ))}
          </Select>
        </div>

        <div className="md:col-span-2 flex items-center space-x-2">
          {renderValueInput()}
          <button
            onClick={handleAddCondition}
            className="bg-green-500 hover:bg-green-600 text-white rounded p-2 flex-shrink-0"
            title="添加条件"
          >
            <FiPlus size={16} />
          </button>
        </div>
      </div>

      <div className="mt-2 text-xs text-gray-500">所有条件将以AND关系组合</div>
    </div>
  );
};

// 主查询条件面板组件
const ConditionsInput = forwardRef(
  (
    {
      modelName = "purchase.order", // 默认为采购订单模型
      companyId,
      setCompanyId,
      queryTimeRange, // 时间范围选择
      setQueryTimeRange, // 设置时间范围
      customConditions,
      setCustomConditions,
      defaultTimeFilterField, // 由模型配置自动获取
    },
    ref,
  ) => {
    // 获取模型配置
    const modelConfig = getModelConfig(modelName);
    // 优先使用props传入的defaultTimeFilterField，否则用模型配置
    const timeFilterField =
      defaultTimeFilterField || modelConfig.defaultTimeFilterField;

    // 暴露方法给父组件
    useImperativeHandle(ref, () => ({
      buildConditions: () => {
        return buildConditions();
      },
    }));

    // 生成查询条件
    const buildConditions = () => {
      const conditionsList = [];

      // 添加公司ID条件
      if (companyId.trim()) {
        conditionsList.push(["company_id", "=", parseInt(companyId)]);
      }

      // 添加自定义条件
      customConditions.forEach((condition) => {
        conditionsList.push([
          condition.field,
          condition.operator,
          condition.value,
        ]);
      });

      // 默认时间限制（如果选择了特定时间范围且无自定义条件）
      if (
        queryTimeRange !== "all" &&
        customConditions.length === 0 &&
        timeFilterField
      ) {
        const pastDate = new Date();
        // 根据选择的时间范围设置不同的日期
        if (queryTimeRange === "month") {
          pastDate.setMonth(pastDate.getMonth() - 1); // 一个月
        } else {
          pastDate.setDate(pastDate.getDate() - 14); // 默认两周内
        }
        conditionsList.push([
          timeFilterField,
          ">=",
          pastDate.toISOString().split("T")[0],
        ]);
      }

      return buildOdooDomainExpression(conditionsList);
    };

    // 处理添加条件
    const handleAddCondition = (newCondition) => {
      setCustomConditions([...customConditions, newCondition]);
    };

    // 删除条件
    const handleDeleteCondition = (id) => {
      setCustomConditions(customConditions.filter((c) => c.id !== id));
    };

    return (
      <div className="mb-4">
        {/* 整合的查询条件区域 */}
        <div className="bg-blue-50 p-4 rounded-lg border">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-medium text-gray-700">查询条件</h3>
          </div>

          {/* 基本条件 */}
          <BasicConditions
            initialCompanyId={companyId}
            initialTimeRange={queryTimeRange}
            hasCustomConditions={customConditions.length > 0}
            onCompanyChange={setCompanyId}
            onTimeRangeChange={setQueryTimeRange}
          />

          {/* 已添加的自定义条件列表 */}
          <ConditionsList
            conditions={customConditions}
            modelName={modelName}
            onDelete={handleDeleteCondition}
          />

          {/* 添加新条件表单 */}
          <AddConditionForm
            modelName={modelName}
            onAddCondition={handleAddCondition}
          />
        </div>
      </div>
    );
  },
);

export default ConditionsInput;
